import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ImageBackground,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth, CONSENT_PROMPT_SKIP_KEY } from '@/contexts/AuthContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import PrimaryButton from '@/components/PrimaryButton';
import OneSignalService from '@/services/oneSignalService';
import { apiService } from '@/services/apiService';
import { TempOnboardingService } from '@/services/tempOnboardingService';
import { ClinicSelectionError, clinicCompletionFeedback } from '@/services/onboardingClinicService';
import { completeAuthenticatedOnboarding } from '@/services/onboardingCompletionClient';
import { OnboardingStateService } from '@/services/onboardingStateService';

export default function OnboardingNotificationsScreen() {
  const { logCompleted } = useOnboardingStepTracking('notifications');
  const router = useRouter();
  const { t } = useLanguage();
  const { user, refreshProfile } = useAuth();
  const insets = useSafeAreaInsets();
  const [isLoading, setIsLoading] = useState(false);
  const completing = useRef(false);
  const mounted = useRef(true);
  const activeUserId = useRef(user?.id);
  activeUserId.current = user?.id;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Durable Google-funnel detection: derive it from the authenticated session
  // provider (restored on cold start) rather than in-memory state, so killing
  // the app mid-funnel doesn't drop the user into the email flow on relaunch.
  const isGoogleUser = user?.app_metadata?.provider === 'google';
  const isEmailUser = user?.app_metadata?.provider === 'email';
  const isAuthenticatedOnboarding = isGoogleUser || isEmailUser;

  // Verified Google/email sessions already own an account. Complete the
  // unfinished profile directly instead of repeating first-access/OTP.
  const completeSessionOnboarding = async () => {
    if (!user) throw new Error('Onboarding session unavailable');
    const assignment = await completeAuthenticatedOnboarding(isGoogleUser ? 'google' : 'email', user.id);
    if (!mounted.current || activeUserId.current !== user.id) return;

    // Google users accept the Terms of Use & Privacy Policy on the Google
    // OAuth consent screen, and the server records that acceptance in
    // /api/auth/google/complete-profile. Suppress the post-login consent sheet
    // for this fresh signup the same way the email flow does: set the one-shot
    // skip flag BEFORE refreshProfile, so the startup prompt queue (rebuilt as
    // soon as the profile loads) doesn't race ahead and pop the sheet.
    if (isGoogleUser) {
      try {
        await AsyncStorage.setItem(CONSENT_PROMPT_SKIP_KEY, 'true');
      } catch {
        console.warn('Consent prompt state unavailable');
      }
    }

    try {
      await refreshProfile();
    } catch {
      console.warn('Onboarding profile refresh unavailable');
    }
    if (!mounted.current || activeUserId.current !== user.id) return;

    if (!assignment.replayed) {
      if (isGoogleUser) {
        import('@/utils/clarityEvents')
          .then(({ logClarityEvent }) => logClarityEvent('google_signup_completed', {}))
          .catch(() => {});
      } else {
        // The legacy password-only path did not count an unfinished signup.
        // Attribute its eventual completion only with current general consent.
        void apiService.get<{ success: boolean; needs_update: boolean }>('/api/consent/status').then(consent => {
          if (activeUserId.current !== user.id || !consent.success ||
              consent.data?.success !== true || consent.data.needs_update !== false) return;
          import('@/utils/clarityEvents')
            .then(({ logAuthSignupCompleted }) => logAuthSignupCompleted({ method: 'email' }))
            .catch(() => {});
          import('@/services/facebookService')
            .then(({ FacebookService }) => FacebookService.logCompleteRegistration('email'))
            .catch(() => {});
        }).catch(() => {});
      }
    }

    await TempOnboardingService.clearData();
    await OnboardingStateService.setAuthenticatedPending(user.id, false).catch(() => {});
    router.replace('/onboarding-success');
  };

  const handleAllowNotifications = async () => {
    if (completing.current) return;
    completing.current = true;
    setIsLoading(true);
    try {
      console.log('🔔 User requesting notification permission...');
      const granted = await OneSignalService.requestPermission();
      if (!mounted.current || activeUserId.current !== user?.id) return;
      console.log('📱 Notification permission result:', granted);

      // PRD §5.2 — `onboarding_step_completed` for the `notifications`
      // step. Fired regardless of grant outcome because forward
      // navigation always proceeds; the OS-level permission state is
      // captured separately in OneSignal/system metrics.
      logCompleted();

      if (isAuthenticatedOnboarding) {
        await completeSessionOnboarding();
        return;
      }

      // Navigate to email step regardless of permission result
      // (User may grant, deny, or have already set permissions previously)
      router.push('/first-access');
    } catch (error) {
      if (!mounted.current || activeUserId.current !== user?.id) return;
      if (error instanceof ClinicSelectionError) {
        const feedback = clinicCompletionFeedback(error.code);
        Alert.alert(t('onboarding.clinic.errorTitle'),
          t(feedback.messageKey),
          feedback.route ? [
            { text: t(feedback.actionKey), onPress: () => router.push(feedback.route!) },
          ] : undefined);
        return;
      }
      console.error('❌ Error requesting notification permission:', error);
      // For an authenticated path, a failed completion should NOT silently drop the
      // user into the email flow — surface the error and let them retry.
      if (isAuthenticatedOnboarding) {
        Alert.alert(t('onboarding.clinic.errorTitle'), t('onboarding.clinic.completeError'));
        setIsLoading(false);
        return;
      }
      // Still navigate forward even if there's an error
      router.push('/first-access');
    } finally {
      completing.current = false;
      if (mounted.current) setIsLoading(false);
    }
  };

  const progressWidth = 95; // Step 10; identity/password follow for email

  return (
    <ImageBackground
      source={require('@/assets/images/onboarding-push.png')}
      style={styles.fullScreenBackground}
      resizeMode="cover"
    >
      <OnboardingLayout progressWidth={progressWidth} transparentHeader={true}>
        <View style={styles.contentContainer}>
          {/* Text Section */}
          <View style={styles.textSection}>
            <Text style={styles.title}>
              {t('onboarding.notifications.title')}
            </Text>
            <Text style={styles.subtitle}>
              {t('onboarding.notifications.subtitle')}
            </Text>
          </View>

          {/* Button Section */}
          <View style={[styles.buttonSection, { paddingBottom: insets.bottom + 24 }]}>
            <PrimaryButton
              title={t('onboarding.notifications.button')}
              onPress={handleAllowNotifications}
              disabled={isLoading}
              loading={isLoading}
              style={styles.primaryButton}
            />
          </View>
        </View>
      </OnboardingLayout>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  fullScreenBackground: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  textSection: {
    alignItems: 'center',
    gap: 24,
    marginBottom: 24,
    paddingHorizontal: 40,
  },
  title: {
    color: '#015480',
    textAlign: 'center',
    fontFamily: 'InstrumentSerif_400Regular',
    fontSize: 40,
    fontWeight: '400',
    lineHeight: 44,
    width: 345,
  },
  titleRegular: {
    fontFamily: 'InstrumentSerif_400Regular',
    fontStyle: 'normal',
  },
  titleItalic: {
    fontFamily: 'InstrumentSerif_400Regular_Italic',
    fontStyle: 'italic',
  },
  subtitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  buttonSection: {
    width: '100%',
    gap: 8,
    paddingHorizontal: 24,
  },
  primaryButton: {
    backgroundColor: '#151515',
  },
});
